import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Users, CalendarCheck, Diamond, Headphones, Star } from "lucide-react";
import { api } from "@/lib/api";
import { CONTINENTS, GENRES, EVENT_TYPES } from "@/lib/data";
import ArtistCard from "@/components/ArtistCard";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HERO = "https://images.unsplash.com/photo-1785336872227-a1e2b44c3cd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CROWD = "https://images.unsplash.com/photo-1589147534012-a853b87508d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  { num: "01", title: "CHOISISSEZ", desc: "Parcourez notre catalogue privé et trouvez l'artiste idéal pour votre événement." },
  { num: "02", title: "RÉSERVEZ", desc: "Remplissez le questionnaire détaillé : date, lieu, logistique et budget." },
  { num: "03", title: "VALIDATION", desc: "L'artiste et son management étudient votre demande et vous envoient un devis." },
  { num: "04", title: "VIVEZ L'EXCEPTION", desc: "Finalisez et profitez d'un moment unique. Nous nous occupons du reste." },
];

const STATS = [
  { icon: Users, value: "500+", label: "Artistes disponibles" },
  { icon: CalendarCheck, value: "2000+", label: "Événements réalisés" },
  { icon: Diamond, value: "98%", label: "Clients satisfaits" },
  { icon: Headphones, value: "24/7", label: "Service client premium" },
];

export default function Home() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [filters, setFilters] = useState({ genre: "all", continent: "all", event: "all" });

  useEffect(() => {
    api.get("/artists", { params: { featured: true } }).then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  const search = () => {
    const p = new URLSearchParams();
    if (filters.genre !== "all") p.set("genre", filters.genre);
    if (filters.continent !== "all") p.set("continent", filters.continent);
    navigate(`/artistes?${p.toString()}`);
  };

  const featured = artists[0];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <p className="text-[#C9A24B] text-xs tracking-[0.3em] uppercase mb-5">
              Célébrités · Événements · Expériences uniques
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05]">
              Votre star.
              <br />
              <span className="gold-text">Votre événement.</span>
            </h1>
            <p className="text-[#C4C1B8] text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
              MYSE vous connecte aux artistes et personnalités du monde entier pour des événements
              vraiment inoubliables.
            </p>
            <Button
              onClick={() => navigate("/artistes")}
              data-testid="hero-cta-btn"
              className="mt-8 gold-bg text-black font-semibold tracking-wide rounded-full px-8 py-6 text-base hover:opacity-90"
            >
              Découvrir les artistes <ArrowRight size={18} className="ml-2" />
            </Button>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10 text-[11px] tracking-[0.2em] uppercase text-[#8A877F]">
              <span>Concerts</span><span className="text-[#C9A24B]">·</span>
              <span>Soirées privées</span><span className="text-[#C9A24B]">·</span>
              <span>Événements d'entreprise</span><span className="text-[#C9A24B]">·</span>
              <span>Expériences VIP</span>
            </div>
          </motion.div>

          {featured && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="glass border border-[#C9A24B]/30 rounded-2xl p-6 max-w-sm ml-auto">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B] mb-3 flex items-center gap-2">
                  <Star size={12} /> Disponible pour vos événements privés
                </p>
                <h3 className="font-serif text-3xl text-white">{featured.name}</h3>
                <p className="text-[#A19E95] text-sm mt-1">{featured.genre} · {featured.continent}</p>
                <img src={featured.image_url} alt={featured.name} className="w-full h-56 object-cover rounded-xl mt-4" />
                <Button
                  onClick={() => navigate(`/artistes/${featured.id}`)}
                  className="w-full mt-4 border border-[#C9A24B]/60 bg-transparent text-[#C9A24B] hover:bg-[#C9A24B]/10 rounded-full"
                >
                  Voir le profil
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="relative -mt-16 z-20 max-w-6xl mx-auto px-6">
        <div className="glass border border-[#C9A24B]/30 rounded-2xl p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 shadow-2xl">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A19E95] mb-1 block">Catégorie</label>
            <Select value={filters.genre} onValueChange={(v) => setFilters({ ...filters, genre: v })}>
              <SelectTrigger data-testid="home-filter-genre" className="bg-[#181820] border-white/10 text-white">
                <SelectValue placeholder="Tous les genres" />
              </SelectTrigger>
              <SelectContent className="bg-[#181820] border-white/10 text-white">
                <SelectItem value="all">Tous les genres</SelectItem>
                {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A19E95] mb-1 block">Continent</label>
            <Select value={filters.continent} onValueChange={(v) => setFilters({ ...filters, continent: v })}>
              <SelectTrigger data-testid="home-filter-continent" className="bg-[#181820] border-white/10 text-white">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-[#181820] border-white/10 text-white">
                <SelectItem value="all">Tous les continents</SelectItem>
                {CONTINENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A19E95] mb-1 block">Type d'événement</label>
            <Select value={filters.event} onValueChange={(v) => setFilters({ ...filters, event: v })}>
              <SelectTrigger data-testid="home-filter-event" className="bg-[#181820] border-white/10 text-white">
                <SelectValue placeholder="Mariage, Soirée..." />
              </SelectTrigger>
              <SelectContent className="bg-[#181820] border-white/10 text-white">
                <SelectItem value="all">Tous les événements</SelectItem>
                {EVENT_TYPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={search} data-testid="home-search-btn" className="w-full gold-bg text-black font-semibold rounded-lg h-10 hover:opacity-90">
              <Search size={16} className="mr-2" /> Rechercher
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURED ARTISTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-serif text-3xl sm:text-4xl text-white">Nos artistes à la une</h2>
          <button onClick={() => navigate("/artistes")} className="text-[#C9A24B] text-sm flex items-center gap-1 hover:gap-2 transition-all">
            Voir tous <ArrowRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {artists.slice(0, 4).map((a, i) => <ArtistCard key={a.id} artist={a} index={i} />)}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <h2 className="font-serif text-3xl sm:text-4xl text-white text-center mb-14">Comment ça fonctionne ?</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="w-12 h-12 rounded-full border border-[#C9A24B]/50 flex items-center justify-center mx-auto mb-5 text-[#C9A24B] font-serif text-xl">
                {i + 1}
              </div>
              <h3 className="text-white font-semibold tracking-wide text-sm uppercase mb-3">{s.title}</h3>
              <p className="text-[#A19E95] text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <s.icon className="text-[#C9A24B]" size={30} />
              <div>
                <div className="font-serif text-3xl gold-text">{s.value}</div>
                <div className="text-[#A19E95] text-xs uppercase tracking-wider">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28">
        <img src={CROWD} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0C]/80" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-[#C9A24B] text-xs tracking-[0.25em] uppercase mb-4">
              Des rêves. Des rencontres. Des moments inoubliables.
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              Prêt à réserver
              <br />
              <span className="gold-text">votre star ?</span>
            </h2>
            <Button
              onClick={() => navigate("/artistes")}
              data-testid="cta-reserver-btn"
              className="mt-8 gold-bg text-black font-semibold rounded-full px-8 py-6 hover:opacity-90"
            >
              Commencer ma réservation <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
          <p className="font-script text-5xl gold-text text-right leading-tight">
            Good People<br />Great Moments
          </p>
        </div>
      </section>
    </div>
  );
}
